import PopUpNotification from '@advanza/interfaces/PopUpNotification'
import { Input } from '@advanza/shadcn-ui/src/components/ui/input'
import { Label } from '@advanza/shadcn-ui/src/components/ui/label'
import { PUSH_FIELDS } from 'hooks/popUpnotificationsHooks'
import {
    countryInputValueToUtc,
    timeZoneLabelForCountry,
    utcToCountryInputValue,
} from 'misc/notificationSendAt'
import { ChangeEvent } from 'react'

const PUSH_TITLE_MAX_LENGTH = 65
const PUSH_BODY_MAX_LENGTH = 180

const INPUT_CLASS =
    'w-full bg-white border border-blue-500 rounded-lg px-4 py-2 text-neutral-800 focus:border-0 focus:border-blue-700 focus:ring-1 focus:ring-blue-500'

interface Props {
    data: PopUpNotification
    onChange: (key: string, value: string | null) => void
}

const PopUpNotificationPushFields = ({ data, onChange }: Props) => {
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.id, event.target.value)
    }

    // push_send_at is held in state as the UTC value the API stores, so the input has to convert
    // both ways; keeping the raw input value in state would re-shift it on every render.
    const handleSendAtChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(
            PUSH_FIELDS.PUSH_SEND_AT,
            countryInputValueToUtc(event.target.value, data.country_code),
        )
    }

    const isScheduled = data.status === 1
    const alreadySent = Boolean(data.push_sent_at)

    return (
        <div className="grid w-full items-start gap-4">
            <div>
                <h3 className="m-0 text-md font-semibold text-neutral-800">Push notification</h3>
                <p className="m-0 text-sm text-neutral-600">
                    Sent to providers with the app installed. The header image above is sent along
                    as the push image: Android shows it, iOS shows the title and message without it.
                </p>
            </div>
            <div className="grid w-full items-start gap-1.5">
                <Label htmlFor={PUSH_FIELDS.PUSH_TITLE}>Push title</Label>
                <Input
                    className={INPUT_CLASS}
                    onChange={handleInputChange}
                    type="text"
                    id={PUSH_FIELDS.PUSH_TITLE}
                    maxLength={PUSH_TITLE_MAX_LENGTH}
                    placeholder="Shown in bold on the lock screen"
                    value={data.push_title ?? ''}
                />
                <span className="text-sm text-neutral-500">
                    {(data.push_title ?? '').length}/{PUSH_TITLE_MAX_LENGTH} characters. Plain text
                    only — markdown is not rendered in a push notification.
                </span>
            </div>
            <div className="grid w-full items-start gap-1.5">
                <Label htmlFor={PUSH_FIELDS.PUSH_BODY}>Push message</Label>
                <Input
                    className={INPUT_CLASS}
                    onChange={handleInputChange}
                    type="text"
                    id={PUSH_FIELDS.PUSH_BODY}
                    maxLength={PUSH_BODY_MAX_LENGTH}
                    placeholder="One or two short sentences"
                    value={data.push_body ?? ''}
                />
                <span className="text-sm text-neutral-500">
                    {(data.push_body ?? '').length}/{PUSH_BODY_MAX_LENGTH} characters. Devices
                    truncate longer messages until the notification is expanded.
                </span>
            </div>
            <div className="grid w-full items-start gap-1.5">
                <Label htmlFor={PUSH_FIELDS.PUSH_SEND_AT}>Send at</Label>
                <Input
                    className={INPUT_CLASS}
                    onChange={handleSendAtChange}
                    type="datetime-local"
                    id={PUSH_FIELDS.PUSH_SEND_AT}
                    value={utcToCountryInputValue(data.push_send_at, data.country_code)}
                />
                <span className="text-sm text-neutral-500">
                    {timeZoneLabelForCountry(data.country_code)}, accurate to within a minute or
                    two.
                </span>
                {!isScheduled && (
                    <span className="text-sm text-neutral-500">
                        Only sent once the status is Active. Triggerable notifications wait for the
                        HubSpot trigger URL instead.
                    </span>
                )}
                {alreadySent && (
                    <span className="text-sm font-semibold text-orange-600">
                        Already sent. Pick a new send time to schedule another send.
                    </span>
                )}
            </div>
        </div>
    )
}

export default PopUpNotificationPushFields
